import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/cn';

const DEFAULT_WIDTH = 400;
const MIN_WIDTH = 320;
const MAX_WIDTH = 1200;

interface SidebarProps {
    children: React.ReactNode;
    className?: string;
    contentClassName?: string;
    defaultWidth?: number;
    disabled?: boolean;
    initialWidth?: number;
    isOpen: boolean;
    maxWidth?: number;
    minWidth?: number;
    onWidthChange?: (width: number) => void;
    side?: 'left' | 'right';
}

export const Sidebar: React.FC<SidebarProps> = ({
    children,
    className,
    contentClassName,
    defaultWidth = DEFAULT_WIDTH,
    disabled,
    initialWidth,
    isOpen,
    maxWidth = MAX_WIDTH,
    minWidth = MIN_WIDTH,
    onWidthChange,
    side = 'right',
}) => {
    const [width, setWidth] = useState(initialWidth ?? defaultWidth);
    // True from the moment isOpen flips false until the width transition
    // finishes. Keeps children mounted so they animate out with the
    // container instead of vanishing instantly.
    const [isClosing, setIsClosing] = useState(false);
    const isDraggingRef = useRef(false);
    const dragStartXRef = useRef(0);
    const dragStartWidthRef = useRef(0);

    useEffect(() => {
        if (initialWidth !== undefined && !isDraggingRef.current) {
            setWidth(initialWidth);
        }
    }, [initialWidth]);

    useEffect(() => {
        if (!isOpen) setIsClosing(true);
    }, [isOpen]);

    const updateWidth = useCallback(
        (nextWidth: number) => {
            setWidth(nextWidth);
            onWidthChange?.(nextWidth);
        },
        [onWidthChange],
    );

    const handleResizeMouseDown = useCallback(
        (e: React.MouseEvent) => {
            if (disabled) return;
            e.preventDefault();
            isDraggingRef.current = true;
            dragStartXRef.current = e.clientX;
            dragStartWidthRef.current = width;
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';

            const onMouseMove = (moveEvent: MouseEvent) => {
                if (!isDraggingRef.current) return;
                const delta =
                    side === 'right'
                        ? dragStartXRef.current - moveEvent.clientX
                        : moveEvent.clientX - dragStartXRef.current;
                const nextWidth = Math.min(maxWidth, Math.max(minWidth, dragStartWidthRef.current + delta));
                updateWidth(nextWidth);
            };

            const onMouseUp = () => {
                isDraggingRef.current = false;
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
            };

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        },
        [maxWidth, minWidth, side, updateWidth, width],
    );

    return (
        <div
            className={cn(
                'border-border bg-background relative flex h-full flex-col overflow-hidden',
                side === 'right' ? 'border-l' : 'border-r',
                !isOpen && side === 'right' && 'overflow-hidden border-l-0',
                !isOpen && side === 'left' && 'overflow-hidden border-r-0',
                className,
            )}
            style={{
                width: isOpen ? width : 0,
                transition: isDraggingRef.current ? undefined : 'width 0.3s',
            }}
            onTransitionEnd={(e) => {
                if (e.propertyName === 'width' && !isOpen) setIsClosing(false);
            }}
        >
            {isOpen && (
                <div
                    className={cn(
                        'absolute top-0 bottom-0 z-20 w-1 transition-colors',
                        disabled
                            ? 'pointer-events-none cursor-default'
                            : 'cursor-ew-resize hover:bg-blue-400/40 active:bg-blue-400/60',
                        side === 'right' ? 'left-0' : 'right-0',
                    )}
                    onMouseDown={handleResizeMouseDown}
                    onDoubleClick={() => updateWidth(defaultWidth)}
                    title="Drag to resize · Double-click to reset"
                />
            )}
            {(isOpen || isClosing) && (
                // Fixed pixel width keeps children from reflowing while the
                // container animates between width and 0.
                <div className={cn('h-full min-h-0 shrink-0', contentClassName)} style={{ width }}>
                    {children}
                </div>
            )}
        </div>
    );
};
